import { FC, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useVersion } from 'mir-utils/api-tools';
import { notNull, notUndefined } from 'mir-utils/utils/utilities';
import { makeElementDraggable } from './makeElementDraggable';
import { HELP_EDDY_DESK_IFRAME_ID } from './help-desk-eddy-iframe-id';

const BUTTON_SUPPORT_CHAT_ID = 'button-support-chat';

type Props = {
  language: 'ru' | 'en';
};

export const IconWidgetHelpDeskEddyChat: FC<Props> = ({ language }) => {
  const version = useVersion();

  const iframeSupportChatRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const iframeSupportChat = notNull(iframeSupportChatRef.current);
    let removeSubscribes: (() => void) | null = null;

    iframeSupportChat.onload = () => {
      const windowSupportChat = notNull(iframeSupportChat.contentWindow);
      const documentSupportChat = notNull(iframeSupportChat.contentDocument);

      const scriptElement = notUndefined(
        documentSupportChat.getElementById('hde-chat-widget')
      ) as HTMLScriptElement;

      scriptElement.defer = true;
      scriptElement.src = '//cdn5.helpdeskeddy.com/js/modules/chat_widget/hde.js';
      scriptElement.dataset.host = 'support.ideco.ru';
      scriptElement.dataset.lang = language;
      scriptElement.dataset.customButton = BUTTON_SUPPORT_CHAT_ID;
      scriptElement.dataset.width = '400';
      scriptElement.dataset.height = '550';

      const buttonSupportChat = documentSupportChat.getElementById(BUTTON_SUPPORT_CHAT_ID);

      const { registerDrag, unRegisterDrag } = makeElementDraggable(
        buttonSupportChat,
        iframeSupportChat
      );

      const isDragRegister = false;

      // Костыль необходим, так как содержимое виджета имеет динамические размеры, а родительский iframe всегда статичен.
      const resizeIframeSupportChat: MutationCallback = () => {
        const containerSupportChat = documentSupportChat.getElementById('hde-container');

        const containerSupportChatStyle =
          containerSupportChat === null
            ? null
            : windowSupportChat.getComputedStyle(containerSupportChat);

        if (buttonSupportChat === null || containerSupportChatStyle === null) {
          return;
        }

        if (containerSupportChatStyle.display !== 'none') {
          buttonSupportChat.style.display = 'none';
          iframeSupportChat.classList.remove('closed');

          iframeSupportChat.width = containerSupportChatStyle.width;
          iframeSupportChat.height = containerSupportChatStyle.height;

          return;
        }

        buttonSupportChat.style.display = 'block';
        iframeSupportChat.classList.add('closed');
        iframeSupportChat.removeAttribute('width');
        iframeSupportChat.removeAttribute('height');

        if (!isDragRegister) {
          registerDrag();
        }
      };

      const observer = new MutationObserver(resizeIframeSupportChat);

      observer.observe(documentSupportChat.body, {
        attributes: true,
        childList: true,
        subtree: true,
      });

      removeSubscribes = () => {
        observer.disconnect();
        unRegisterDrag();
      };
    };

    return () => {
      removeSubscribes?.();
    };
  }, [language, version]);

  return createPortal(
    <iframe
      title="help-desk-eddy"
      id={HELP_EDDY_DESK_IFRAME_ID}
      className="closed"
      src="help-desk-eddy/index.html"
      ref={iframeSupportChatRef}
    />,
    document.body
  );
};
